(() => {
  if (window.ABLProductSticky) {
    window.ABLProductSticky.init(document);
    return;
  }

  const selector = '[data-abl-pdp-sticky]';
  const states = new Map();
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const supported = 'IntersectionObserver' in window;
  let menuOpen = document.documentElement.classList.contains('ablp-menu-open');
  let frame = 0;
  let fallbackListening = false;

  const isRendered = (node) => node && node.getClientRects().length > 0;
  const findPurchaseArea = () => document.querySelector('[data-pi] form[data-product-form]') || document.getElementById('pdp-form');

  const findSelection = (area) => {
    if (!area) return null;
    const candidates = [
      area.querySelector('recharge-subscription-widget'),
      area.querySelector('[data-pi-sub-view]'),
      area.querySelector('[data-pi-ot-view]'),
      area.querySelector('[data-pi-vimage]')?.closest('.pi__vimages'),
      area
    ];
    return candidates.find(isRendered) || null;
  };

  const setAccessible = (state, accessible) => {
    state.bar.setAttribute('aria-hidden', String(!accessible));
    state.bar.inert = !accessible;
    state.button.tabIndex = accessible ? 0 : -1;
  };

  const cancelEntrance = (state) => {
    window.clearTimeout(state.introTimer);
    state.introTimer = 0;
    state.bar.classList.remove('is-entering');
  };

  const finishEntrance = (state) => {
    cancelEntrance(state);
    setAccessible(state, state.visible && state.bar.isConnected && !menuOpen && !document.hidden);
  };

  const startEntrance = (state) => {
    const style = window.getComputedStyle(state.bar);
    const travel = Math.max(0, state.bar.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) - state.image.offsetWidth);
    state.bar.style.setProperty('--pdp-sticky-travel', `${travel.toFixed(1)}px`);
    state.bar.classList.add('is-entering');
    state.introTimer = window.setTimeout(() => finishEntrance(state), 935);
  };

  const setVisible = (state) => {
    const show = state.passed && !!state.area?.isConnected && !menuOpen && !document.hidden;
    if (state.visible === show) return;
    cancelEntrance(state);
    state.visible = show;
    const entering = show && !!state.image && !preference.matches;
    if (entering) startEntrance(state);
    state.bar.classList.toggle('is-visible', show);
    setAccessible(state, show && !entering);
  };

  const evaluate = () => {
    frame = 0;
    const measurements = [...states.values()].map((state) => ({
      state,
      passed: !!state.area?.isConnected && state.area.getBoundingClientRect().bottom <= 0
    }));
    measurements.forEach(({ state, passed }) => {
      state.passed = passed;
      setVisible(state);
    });
  };

  const requestEvaluate = () => {
    if (!frame && states.size && !document.hidden) frame = window.requestAnimationFrame(evaluate);
  };

  const syncFallback = () => {
    const active = !supported && states.size > 0 && !document.hidden;
    if (active && !fallbackListening) {
      window.addEventListener('scroll', requestEvaluate, { passive: true });
      window.addEventListener('resize', requestEvaluate, { passive: true });
    } else if (!active && fallbackListening) {
      window.removeEventListener('scroll', requestEvaluate);
      window.removeEventListener('resize', requestEvaluate);
    }
    fallbackListening = active;
    if (!active && frame) {
      window.cancelAnimationFrame(frame);
      frame = 0;
    }
  };

  const bindPurchaseArea = (state) => {
    const area = findPurchaseArea();
    if (area === state.area) return;
    state.observer?.disconnect();
    state.area = area;
    state.passed = false;
    if (area) {
      state.passed = area.getBoundingClientRect().bottom <= 0;
      state.observer?.observe(area);
    }
    setVisible(state);
  };

  const scrollToSelection = (state, event) => {
    bindPurchaseArea(state);
    const target = findSelection(state.area);
    if (!target) return;
    event.preventDefault();
    state.focusCleanup?.();
    const originalTabIndex = target.getAttribute('tabindex');
    if (originalTabIndex === null) target.setAttribute('tabindex', '-1');
    const cleanup = () => {
      if (originalTabIndex === null) target.removeAttribute('tabindex');
      target.removeEventListener('blur', cleanup);
      state.focusCleanup = null;
    };
    state.focusCleanup = cleanup;
    target.addEventListener('blur', cleanup, { once: true });
    target.focus({ preventScroll: true });

    const header = document.querySelector('[data-ablp-header]');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - headerBottom - 18);
    if (preference.matches) {
      const root = document.documentElement;
      const oldBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo({ top, behavior: 'auto' });
      root.style.scrollBehavior = oldBehavior;
    } else {
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const initialize = (scope = document) => {
    const bars = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) bars.unshift(scope);
    bars.forEach((bar) => {
      if (states.has(bar)) return;
      const button = bar.querySelector('[data-abl-pdp-sticky-button]');
      if (!button) return;
      const state = {
        bar, button, image: bar.querySelector('.abl-pdp-sticky__image'),
        area: null, passed: false, visible: false, introTimer: 0
      };
      state.click = (event) => scrollToSelection(state, event);
      button.addEventListener('click', state.click);
      if (supported) {
        state.observer = new IntersectionObserver((entries) => {
          const entry = entries[entries.length - 1];
          if (!entry || entry.target !== state.area) return;
          state.passed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          setVisible(state);
        }, { threshold: 0 });
      }
      states.set(bar, state);
    });
    states.forEach(bindPurchaseArea);
    syncFallback();
    requestEvaluate();
  };

  const destroy = (scope) => {
    states.forEach((state, bar) => {
      if (scope === bar || scope.contains(bar)) {
        cancelEntrance(state);
        state.observer?.disconnect();
        state.button.removeEventListener('click', state.click);
        state.focusCleanup?.();
        states.delete(bar);
      } else if (state.area && scope.contains(state.area)) {
        state.observer?.disconnect();
        state.area = null;
        state.passed = false;
        state.focusCleanup?.();
        setVisible(state);
      }
    });
    syncFallback();
  };

  window.ABLProductSticky = { init: initialize, destroy };
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:section:reorder', requestEvaluate);
  document.addEventListener('abl:menu-toggle', (event) => {
    menuOpen = !!event.detail?.open;
    states.forEach(setVisible);
  });
  document.addEventListener('visibilitychange', () => {
    states.forEach(setVisible);
    syncFallback();
    requestEvaluate();
  });
  preference.addEventListener('change', () => {
    if (preference.matches) states.forEach(finishEntrance);
  });
  window.addEventListener('pageshow', () => {
    states.forEach(bindPurchaseArea);
    requestEvaluate();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }
})();
